'use client';

import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MoreHorizontal,
} from 'lucide-react';
import React from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  showFirstLast?: boolean;
  showPrevNext?: boolean;
  maxVisiblePages?: number;
  className?: string;
}

interface PaginationItemProps {
  children: React.ReactNode;
  isActive?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
  className?: string;
}

const PaginationItem = React.forwardRef<HTMLButtonElement, PaginationItemProps>(
  ({ children, isActive, isDisabled, onClick, className, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        variant={isActive ? 'default' : 'ghost'}
        size="sm"
        type="button"
        disabled={isDisabled}
        onClick={onClick}
        className={cn('h-9 w-9 rounded-full p-0', isActive && 'pointer-events-none', className)}
        {...props}
      >
        {children}
      </Button>
    );
  }
);
PaginationItem.displayName = 'PaginationItem';

const PaginationEllipsis = ({ className }: { className?: string }) => (
  <div className={cn('flex h-9 w-9 items-center justify-center', className)}>
    <MoreHorizontal className="h-4 w-4" />
    <span className="sr-only">More pages</span>
  </div>
);

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  showFirstLast = true,
  showPrevNext = true,
  maxVisiblePages = 5,
  className,
}: PaginationProps) {
  if (totalPages <= 1) return null;

  const getVisiblePages = () => {
    const pages: (number | 'ellipsis')[] = [];

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      const startPage = Math.max(2, currentPage - Math.floor(maxVisiblePages / 2));
      const endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 3);

      // Add ellipsis after first page if needed
      if (startPage > 2) {
        pages.push('ellipsis');
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i);
        }
      }

      // Add ellipsis before last page if needed
      if (endPage < totalPages - 1) {
        pages.push('ellipsis');
      }

      // Always show last page if more than 1 page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const visiblePages = getVisiblePages();

  return (
    <nav
      role="navigation"
      aria-label="pagination"
      className={cn('mx-auto flex w-full justify-center', className)}
    >
      <div className="flex flex-row items-center gap-1">
        {/* First page button */}
        {showFirstLast && (
          <PaginationItem isDisabled={currentPage === 1} onClick={() => onPageChange(1)}>
            <ChevronsLeft className="h-4 w-4" />
            <span className="sr-only">Go to first page</span>
          </PaginationItem>
        )}

        {/* Previous page button */}
        {showPrevNext && (
          <PaginationItem
            isDisabled={currentPage === 1}
            onClick={() => onPageChange(currentPage - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">Go to previous page</span>
          </PaginationItem>
        )}

        {/* Page numbers */}
        {visiblePages.map((page, index) => {
          if (page === 'ellipsis') {
            return <PaginationEllipsis key={`ellipsis-${index}`} />;
          }

          return (
            <PaginationItem
              key={page}
              isActive={currentPage === page}
              onClick={() => onPageChange(page)}
            >
              {page}
            </PaginationItem>
          );
        })}

        {/* Next page button */}
        {showPrevNext && (
          <PaginationItem
            isDisabled={currentPage === totalPages}
            onClick={() => onPageChange(currentPage + 1)}
          >
            <ChevronRight className="h-4 w-4" />
            <span className="sr-only">Go to next page</span>
          </PaginationItem>
        )}

        {/* Last page button */}
        {showFirstLast && (
          <PaginationItem
            isDisabled={currentPage === totalPages}
            onClick={() => onPageChange(totalPages)}
          >
            <ChevronsRight className="h-4 w-4" />
            <span className="sr-only">Go to last page</span>
          </PaginationItem>
        )}
      </div>
    </nav>
  );
}

// Export individual components for custom usage
export { PaginationEllipsis, PaginationItem };
